import React, { useState, useCallback, useEffect } from 'react';
import { Spinner } from '../../../shared/components/ui';
import { estimatesApi } from '../../../shared/services/estimatesApi';

/**
 * Allowed file types for supporting documents
 */
const ALLOWED_TYPES = {
  'application/pdf': 'PDF',
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPEG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'application/msword': 'DOC',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/vnd.ms-excel': 'XLS',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'text/plain': 'TXT',
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB - blueprint PDFs can be 50-90MB

/**
 * Format file size for display
 */
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Get file icon based on content type
 */
function getFileIcon(contentType) {
  if (contentType?.startsWith('image/')) {
    return (
      <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  if (contentType === 'application/pdf') {
    return (
      <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
  return (
    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

/**
 * Supporting Documents Uploader Component
 * Allows uploading multiple files with automatic processing
 */
export function SupportingDocumentsUploader({
  estimateId,
  onDocumentsChange,
  disabled = false,
}) {
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  const [budget, setBudget] = useState(null); // { maxPages, pagesUsed, pagesRemaining }

  const refreshBudget = useCallback(async () => {
    if (!estimateId) return;
    try {
      const result = await estimatesApi.getEstimatePageBudget(estimateId);
      setBudget(result);
    } catch (err) {
      console.warn('Failed to fetch page budget:', err);
    }
  }, [estimateId]);

  useEffect(() => {
    refreshBudget();
  }, [refreshBudget]);

  // Notify parent whenever documents change (including staged/pending ones)
  useEffect(() => {
    if (onDocumentsChange) {
      onDocumentsChange(documents.filter((d) => d.status !== 'error'));
    }
  }, [documents, onDocumentsChange]);

  // Pages already counted but not yet started — they're committed against the
  // budget at start-processing time, so we surface them in the meter too.
  const pendingPages = documents.reduce(
    (sum, d) => sum + (d.totalPages || 0),
    0,
  );
  const budgetExhausted = budget != null && budget.pagesRemaining <= 0;

  /**
   * Upload a single document and update its state on completion
   */
  const uploadDocument = useCallback(async (doc) => {
    if (!estimateId) {
      return;
    }

    try {
      const result = await estimatesApi.uploadSupportingDocument(
        estimateId,
        doc.file,
        (progress) => {
          setDocuments((prev) =>
            prev.map((d) =>
              d.id === doc.id ? { ...d, progress } : d
            )
          );
        }
      );

      setDocuments((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? {
                ...d,
                status: 'uploaded',
                progress: 100,
                documentId: result.documentId,
                totalPages: result.totalPages ?? null,
              }
            : d
        )
      );
      refreshBudget();
    } catch (err) {
      console.error('Upload failed:', err);
      setDocuments((prev) =>
        prev.map((d) =>
          d.id === doc.id
            ? { ...d, status: 'error', error: err.message || 'Upload failed' }
            : d
        )
      );
    }
  }, [estimateId, refreshBudget]);

  /**
   * Handle file selection — validates then immediately uploads each file
   */
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    setError(null);

    const newDocs = [];
    const errors = [];

    for (const file of files) {
      // Validate file type
      if (!ALLOWED_TYPES[file.type]) {
        errors.push(`${file.name}: File type not supported`);
        continue;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File size exceeds 100MB limit`);
        continue;
      }

      newDocs.push({
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        status: 'uploading', // uploading, uploaded, processing, error
        progress: 0,
        error: null,
      });
    }

    if (errors.length > 0) {
      setError(errors.join('; '));
    }

    if (newDocs.length > 0) {
      setDocuments((prev) => [...prev, ...newDocs]);
      // Immediately upload each new document
      for (const doc of newDocs) {
        uploadDocument(doc);
      }
    }

    // Reset input
    e.target.value = '';
  }, [uploadDocument]);

  /**
   * Remove a document
   */
  const handleRemoveDocument = useCallback((docId) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== docId));
  }, []);

  return (
    <div className="space-y-4">
      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Page Budget Meter */}
      {budget && (
        <div className="text-xs text-gray-600 dark:text-gray-300">
          <span className="font-medium">
            {budget.pagesUsed + pendingPages} / {budget.maxPages} pages used
          </span>
          {budgetExhausted && (
            <span className="ml-2 text-amber-700 dark:text-amber-400">
              — Page budget exhausted. Upgrade plan to add more documents.
            </span>
          )}
        </div>
      )}

      {/* File Input */}
      {!budgetExhausted && (
        <div className="flex items-center gap-3">
          <label
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg cursor-pointer transition-colors
              ${disabled
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
              }
            `}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Documents
            <input
              type="file"
              multiple
              accept={Object.keys(ALLOWED_TYPES).join(',')}
              onChange={handleFileSelect}
              disabled={disabled}
              className="hidden"
            />
          </label>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            PDF, Images, DOC, XLS (max 100MB each)
          </span>
        </div>
      )}

      {/* Documents List */}
      {documents.length > 0 && (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className={`flex items-start gap-3 p-3 border rounded-lg
                ${doc.status === 'error' ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30' : 'border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900'}
              `}
            >
              {/* File Icon */}
              <div className="flex-shrink-0">
                {getFileIcon(doc.file.type)}
              </div>

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {doc.file.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    ({formatFileSize(doc.file.size)})
                  </span>
                </div>

                {/* Upload Progress */}
                {doc.status === 'uploading' && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      <span className="text-xs text-gray-600 dark:text-gray-300">Uploading...</span>
                    </div>
                    <div className="mt-1 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${doc.progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Uploaded Status */}
                {doc.status === 'uploaded' && (
                  <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Uploaded
                  </div>
                )}

                {/* Processing Status */}
                {doc.status === 'processing' && (
                  <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Uploaded — processing will begin shortly</span>
                  </div>
                )}

                {/* Error Status */}
                {doc.status === 'error' && (
                  <div className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {doc.error || 'Upload failed'}
                  </div>
                )}
              </div>

              {/* Remove Button (always visible except during active upload) */}
              {doc.status !== 'uploading' && (
                <button
                  type="button"
                  onClick={() => handleRemoveDocument(doc.id)}
                  className="flex-shrink-0 p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-200"
                  title="Remove"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {documents.length === 0 && (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          No supporting documents added yet
        </div>
      )}
    </div>
  );
}

export default SupportingDocumentsUploader;
